import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowRight,
  Layers,
  Database,
  BarChart3,
  Headphones,
  RefreshCw,
  Cloud,
  TrendingUp,
  Target,
  ShieldCheck,
  Gauge,
  Infinity as InfinityIcon,
} from "lucide-react";

const CATEGORIES = [
  {
    icon: Layers,
    category: "SAP Implementation & Migration",
    intro:
      "We guide your adoption of SAP — including SAP S/4HANA — using proven roadmaps with minimal disruption, and migrate legacy systems to cloud-ready environments that power smarter processes with real-time analytics and AI-driven insights.",
    services: [
      {
        title: "Implementation",
        description:
          "End-to-end SAP implementation across modules — from blueprint and configuration to go-live and hypercare.",
        outcome: "A live, stable SAP environment fit for your business.",
      },
      {
        title: "Migration to HANA",
        description:
          "Move legacy SAP ECC and non-SAP systems onto HANA / S/4HANA with minimal business disruption and optimised data models.",
        outcome: "In-memory speed, simplified landscapes, real-time decisions.",
      },
      {
        title: "Roll-outs",
        description:
          "Multi-entity, multi-country and multi-plant SAP roll-outs with templated delivery and localised configurations.",
        outcome: "Consistent operations across every site and entity.",
      },
    ],
  },
  {
    icon: Database,
    category: "SAP S/4HANA Enablement",
    intro:
      "Enable next-generation ERP with S/4HANA. From system configuration to custom Fiori applications, we optimise data models, automate workflows, and deliver in-memory performance for rapid decisions and business agility.",
    services: [
      {
        title: "S/4HANA Configuration",
        description:
          "Module-level configuration across FICO, MM, SD, PP and more — tailored to your processes, not generic templates.",
        outcome: "Processes that mirror how your business actually runs.",
      },
      {
        title: "Custom Fiori Apps",
        description:
          "Design and build SAP Fiori applications that put real-time data and approvals into the hands of business users.",
        outcome: "Faster decisions and a measurably better user experience.",
      },
      {
        title: "Workflow Automation",
        description:
          "Automate procurement, finance and HR workflows inside S/4HANA — replacing manual approvals with intelligent routing.",
        outcome: "Hours saved per week. Audit-ready process trails.",
      },
    ],
  },
  {
    icon: Cloud,
    category: "RISE with SAP & Public Cloud",
    intro:
      "Move SAP onto the cloud with confidence. We help organisations evaluate, plan and execute RISE with SAP and public-cloud SAP deployments — including subscription model design, landscape architecture, and the change management required for a cloud-first ERP.",
    services: [
      {
        title: "RISE with SAP Adoption",
        description:
          "End-to-end RISE with SAP advisory and execution — from commercial structuring and licence sizing to S/4HANA Cloud configuration, data migration and go-live.",
        outcome: "A subscription-based, fully managed SAP estate without the surprises.",
      },
      {
        title: "SAP Public Cloud Deployment",
        description:
          "Deploy and run SAP S/4HANA Public Cloud (and other SAP cloud editions) with hardened best-practice configurations, integrations and identity setup.",
        outcome: "Fast time-to-value on a standardised, vendor-managed cloud.",
      },
      {
        title: "Cloud Migration & Re-platforming",
        description:
          "Plan and execute moves from on-premise SAP to public-cloud or hyperscaler-hosted environments — covering assessment, sizing, cutover, and post-go-live optimisation.",
        outcome: "Lower TCO, predictable performance, modern operating model.",
      },
    ],
  },
  {
    icon: BarChart3,
    category: "Business Analytics & Reporting",
    intro:
      "We integrate your data into interactive dashboards and predictive reports — empowering teams with clear visibility into trends and operations. Our analytics services accelerate smart, evidence-led decision-making across the business.",
    services: [
      {
        title: "BI / BO Implementation",
        description:
          "Design and deploy SAP BusinessObjects and BI dashboards that consolidate data from SAP and non-SAP sources.",
        outcome: "A single source of truth for leadership reporting.",
      },
      {
        title: "Big Data Analytics",
        description:
          "Build analytics pipelines on top of your SAP data — from ETL to predictive models to executive dashboards.",
        outcome: "Forecasts your team can plan against, not just react to.",
      },
      {
        title: "GST & Tax Reporting",
        description:
          "Configure and automate GST and statutory tax reporting inside SAP — accurate, timely, and audit-ready.",
        outcome: "Compliance that runs on autopilot.",
      },
    ],
  },
  {
    icon: Headphones,
    category: "Application Management Services (AMS)",
    intro:
      "Keep your SAP systems performing at peak with our round-the-clock support. We handle incident resolution, updates, change requests, and ongoing user capability building — so your internal team can focus on growth, not firefighting.",
    services: [
      {
        title: "Support on Demand",
        description:
          "Flexible, on-demand SAP support across modules — pay for what you use, when you need it.",
        outcome: "Expert help when you need it. No retainer overhead.",
      },
      {
        title: "System Audit",
        description:
          "Comprehensive audits of your SAP landscape — security, performance, configuration, and data integrity.",
        outcome: "A clear remediation roadmap and stronger compliance posture.",
      },
      {
        title: "Mobility Solutions",
        description:
          "Extend SAP to mobile — approvals, dashboards, and field operations through SAP Fiori and custom apps.",
        outcome: "SAP that works wherever your people do.",
      },
    ],
  },
  {
    icon: RefreshCw,
    category: "SAP Upgrades & Enhancements",
    intro:
      "Stay current with upgrade plans tailored to your tech stack. We ensure seamless transitions, thorough testing, and user capability building — unlocking the latest SAP innovations and stronger security with every release.",
    services: [
      {
        title: "Version Upgrades",
        description:
          "Plan and execute SAP version upgrades with full impact analysis, regression testing, and zero-surprise cutovers.",
        outcome: "A modern, supported SAP estate ready for what's next.",
      },
      {
        title: "SAP B1 & ByD",
        description:
          "Specialist enhancement, customisation and support for SAP Business One and Business ByDesign deployments.",
        outcome: "ERP that grows with your mid-market business.",
      },
      {
        title: "Continuous Enhancement",
        description:
          "Quarterly enhancement releases — new features, optimisations and integrations driven by your business roadmap.",
        outcome: "An SAP system that compounds in value year over year.",
      },
    ],
  },
];

const Services = () => {
  return (
    <div data-testid="services-page">
      {/* PAGE HERO */}
      <section
        data-testid="services-hero"
        className="bg-[#F5F5DC] pt-32 pb-24 lg:pt-40 lg:pb-32"
      >
        <div className="container-pro grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <p className="overline mb-6 fade-up">SAP Consulting Services</p>
            <h1 className="font-display text-5xl lg:text-7xl text-[#111111] leading-[1.05] fade-up-delay-1">
              SAP, analytics and AMS —
              <br />
              <span className="text-[#8B0000]">delivered as one.</span>
            </h1>
          </div>
          <div className="lg:col-span-5 flex items-end fade-up-delay-2">
            <p className="text-lg text-[#4A4A4A] leading-relaxed">
              Our expertise delivers comprehensive support across the entire
              SAP landscape from implementation and migration to analytics,
              application management services (AMS), and ongoing optimisation.
              Most engagements extend across multiple practices, as meaningful
              outcomes are achieved through the seamless integration of
              strategy, system configuration, and sustained operational
              excellence.
            </p>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      {CATEGORIES.map((cat, idx) => {
        const Icon = cat.icon;
        const isAlt = idx % 2 === 1;
        return (
          <section
            key={cat.category}
            data-testid={`service-category-${cat.category}`}
            className={`section ${isAlt ? "bg-[#F5F5DC]" : "bg-white"}`}
          >
            <div className="container-pro">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
                <div className="lg:col-span-6">
                  <Icon
                    size={40}
                    strokeWidth={1.5}
                    className="text-[#8B0000] mb-6"
                  />
                  <p className="overline mb-4">{`Practice 0${idx + 1}`}</p>
                  <h2 className="font-display text-4xl lg:text-5xl text-[#111111] leading-tight">
                    {cat.category}
                  </h2>
                </div>
                <div className="lg:col-span-6 flex items-end">
                  <p className="text-lg text-[#4A4A4A] leading-relaxed">
                    {cat.intro}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E5E5E5] border border-[#E5E5E5]">
                {cat.services.map((s) => (
                  <article
                    key={s.title}
                    data-testid={`service-item-${s.title}`}
                    className={`p-10 ${isAlt ? "bg-[#F5F5DC]" : "bg-white"} hover:bg-white/60 transition-colors duration-300 flex flex-col`}
                  >
                    <h3 className="font-display text-2xl text-[#111111] mb-4 leading-snug">
                      {s.title}
                    </h3>
                    <p className="text-[#4A4A4A] leading-relaxed mb-6 flex-1">
                      {s.description}
                    </p>
                    <p className="text-sm text-[#111111] border-t border-[#111111]/15 pt-4">
                      <span className="text-[#8B0000] uppercase tracking-[0.2em] text-[0.65rem] mr-2 block mb-1">
                        Outcome
                      </span>
                      {s.outcome}
                    </p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {/* ROI / VALUE — premium beige section */}
      <section
        data-testid="services-roi-section"
        className="section bg-[#F5F5DC]"
      >
        <div className="container-pro">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-5">
              <p className="overline mb-5 fade-up">SAP ROI &amp; Value</p>
              <h2 className="font-display text-4xl lg:text-5xl text-[#111111] leading-[1.1] fade-up-delay-1">
                Turn your SAP investment into
                <br />
                <span className="text-[#8B0000]">measurable business value.</span>
              </h2>
              <p className="text-lg text-[#4A4A4A] leading-relaxed mt-6 fade-up-delay-2">
                Our implementation model ensures every investment in SAP
                delivers efficiency, performance, and long-term growth.
              </p>

              <div className="mt-10 fade-up-delay-3">
                <p className="text-[#4A4A4A] leading-relaxed">
                  We treat SAP as a strategic investment rather than a
                  one-time deployment. Every engagement begins by aligning
                  the SAP solution with your core business objectives — then
                  shaping the rollout to maximise ROI through process
                  optimisation, faster cycle times, and reduced operational
                  drag. By compressing implementation timelines, minimising
                  downtime, and accelerating value realisation, we help your
                  team move from go-live to genuine business outcomes faster.
                </p>
                <p className="text-[#4A4A4A] leading-relaxed mt-4">
                  Scalability, seamless integration, and high user adoption
                  are built into every design decision. We collaborate
                  closely with executives, IT leadership, and end users —
                  ensuring measurable outcomes are owned by the business and
                  sustained long after the system goes live.
                </p>
              </div>
            </div>

            <div className="lg:col-span-7">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#111111]/15 border border-[#111111]/15">
                {[
                  {
                    icon: TrendingUp,
                    title: "ROI-driven implementation approach",
                    body: "Every milestone tied to a measurable business outcome — not just technical sign-off.",
                  },
                  {
                    icon: Target,
                    title: "Strategy aligned with technology",
                    body: "SAP designed around your operating model, not the other way around.",
                  },
                  {
                    icon: ShieldCheck,
                    title: "Deep SAP &amp; enterprise expertise",
                    body: "Cross-vertical experience across HANA, S/4, B1, ByD, BI/BO and infrastructure.",
                  },
                  {
                    icon: Gauge,
                    title: "Performance &amp; cost efficiency",
                    body: "Tuned configurations and process redesign that lift throughput and reduce TCO.",
                  },
                  {
                    icon: InfinityIcon,
                    title: "End-to-end support",
                    body: "From first workshop to continuous improvement, we stay engaged through every phase.",
                  },
                  {
                    icon: Layers,
                    title: "Stakeholder-led delivery",
                    body: "Collaboration with executives, IT, and end users to drive adoption and accountability.",
                  },
                ].map((point, i) => {
                  const Icon = point.icon;
                  return (
                    <li
                      key={point.title}
                      data-testid={`roi-point-${i}`}
                      className="bg-[#F5F5DC] p-7 lg:p-8 group hover:bg-white transition-colors duration-300 fade-up"
                      style={{ animationDelay: `${100 + i * 90}ms` }}
                    >
                      <div className="w-11 h-11 bg-white border border-[#8B0000]/20 flex items-center justify-center mb-5 transition-all duration-300 group-hover:bg-[#8B0000] group-hover:border-[#8B0000] group-hover:scale-105">
                        <Icon
                          size={20}
                          strokeWidth={1.5}
                          className="text-[#8B0000] transition-colors duration-300 group-hover:text-white"
                        />
                      </div>
                      <h3
                        className="font-display text-xl text-[#111111] mb-2 leading-snug"
                        dangerouslySetInnerHTML={{ __html: point.title }}
                      />
                      <p
                        className="text-[#4A4A4A] text-sm leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: point.body }}
                      />
                    </li>
                  );
                })}
              </ul>

              <div
                data-testid="roi-differentiation-statement"
                className="mt-10 border-l-2 border-[#8B0000] pl-6 lg:pl-8 fade-up-delay-3"
              >
                <p className="font-display text-2xl lg:text-3xl text-[#111111] leading-snug">
                  With Anila Consulting, SAP is not just implemented —
                  <span className="text-[#8B0000]"> it is optimised to deliver consistent, measurable value.</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        data-testid="services-cta"
        className="bg-[#8B0000] text-white py-24 lg:py-32"
      >
        <div className="container-pro grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-display text-4xl lg:text-6xl leading-tight text-white">
              Start maximising your SAP
              <br /> investment today.
            </h2>
            <p className="text-white/80 mt-6 text-lg max-w-2xl leading-relaxed">
              Tell us about your environment, your objectives, and the
              outcomes you're chasing. A senior consultant will respond
              personally with a clear, costed plan within one business day.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link
              to="/contact"
              data-testid="services-cta-button"
              className="btn-ghost-light"
            >
              Get in Touch <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
